import type { RouteLocationNormalizedLoaded, RouteLocationNormalized } from 'vue-router';
import request, { getAxiosBaseURL, getAxiosHeader, getModel, getUserIdForHeaders } from '@/utils/request';
import { getStorage, STORAGE_KEYS } from '@/utils/storage';
import useSubscriptionStore from '@/store/subscription';
import type { UserSubscribeInfo } from '@/store/subscription';
import { normalizeVideoType } from '@/utils/mixpanel';
import { fetchFeatureFlags, useFeatureFlags } from '@/utils/useFeatureFlags';

export type WatchLimitEvent = 'query' | 'start' | 'heartbeat' | 'stop';

export interface WatchLimitPayload {
  id: string;
  type: 0 | 1;
  liveFlag: 0 | 1;
}

export interface WatchLimitResult {
  acquired: boolean;
  active_count: number;
  interval?: number;
}

export interface WatchLimitContext extends WatchLimitPayload {
  contentId: string;
  videoTitle: string;
  videoType: string;
  variant: 'subscribed' | 'unsubscribed';
  wallType: 'absolute_limit' | 'subscription_redirection';
  subscriptionStatus: 'subscribed' | 'unsubscribed';
  subscriptionPlan: string;
}

export interface PendingWatchLimitContext extends WatchLimitContext {
  sessionId: string;
  source: 'pre_navigation' | 'playback';
}

type RouteLike = Pick<RouteLocationNormalizedLoaded, 'query'> | Pick<RouteLocationNormalized, 'query'>;

const WATCH_LIMIT_ENDPOINT = '/bff/live/c/broad_cast_periodic_data.json';
const WATCH_LIMIT_CONTEXT_STORAGE_KEY = 'watch-limit-context';

function getQueryValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value[0] ? String(value[0]) : '';
  }
  if (value === undefined || value === null) return '';
  return String(value);
}

function getBody(cardInfo?: any) {
  return cardInfo?.data?.body || {};
}

function getVideoTitle(body: Record<string, any>, liveFlag: 0 | 1): string {
  if (liveFlag === 1) {
    return (
      body.match_info?.competition?.match_title || body.match_info?.competition?.name || body.match_info?.title || ''
    );
  }

  return body.clip_info?.name || body.episode_info?.title || body.movie_info?.title || '';
}

function getVideoType(body: Record<string, any>, liveFlag: 0 | 1): string {
  if (liveFlag === 1) {
    return normalizeVideoType('', true, Number(body.match_info?.match_type ?? 0));
  }

  if (body.movie_info) {
    return 'movie';
  }
  if (body.episode_info) {
    return 'episode';
  }

  return normalizeVideoType(body.clip_info?.category_name, false);
}

function buildRequestBody(event: WatchLimitEvent, payload: WatchLimitPayload) {
  return {
    actions: ['watchLimit'],
    pay_prompt_request: {
      match_id: payload.id,
    },
    watch_limit_request: {
      event,
    },
    live_end_prompt_request: {
      id: payload.id,
      type: payload.type,
    },
  };
}

function normalizeResult(response: any): WatchLimitResult {
  const limit = response?.data?.watch_limit ?? response?.data?.live_limit ?? null;

  return {
    acquired: limit?.acquired ?? true,
    active_count: Number(limit?.active_count ?? 0),
    interval: typeof limit?.interval === 'number' ? limit.interval : undefined,
  };
}

function getLanguageHeader(): string {
  const locale = getStorage<string>(STORAGE_KEYS.LOCALE);
  return locale === 'en' ? '0' : '1';
}

async function requestWatchLimit(event: WatchLimitEvent, payload: WatchLimitPayload): Promise<WatchLimitResult> {
  const response: any = await request.post(WATCH_LIMIT_ENDPOINT, buildRequestBody(event, payload), {
    skipCodeCheck: true,
  });

  if (response?.error_code && response.error_code !== 10000) {
    throw new Error(response.error || 'Watch limit request failed');
  }

  return normalizeResult(response);
}

export async function stopWatchLimitKeepalive(payload: WatchLimitPayload): Promise<boolean> {
  try {
    const baseURL = getAxiosBaseURL() || '';
    const endpoint = `${baseURL}${WATCH_LIMIT_ENDPOINT}`;
    const gsid = String(getAxiosHeader('gsid') || getStorage(STORAGE_KEYS.GSID) || '');
    const body = buildRequestBody('stop', payload);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      v_p: String(getAxiosHeader('v_p') || '1.12.0'),
      os: String(getAxiosHeader('os') || process.env.VUE_APP_API_IDENTIFIER || ''),
      model: String(getAxiosHeader('model') || getModel()),
      brand: String(getAxiosHeader('brand') || getModel()),
      ua: String(getAxiosHeader('ua') || navigator.userAgent),
      uid: String(getUserIdForHeaders() || ''),
      language: getLanguageHeader(),
    };

    if (gsid) {
      headers.gsid = gsid;
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      keepalive: true,
      body: JSON.stringify(body),
    });

    return response.ok;
  } catch (error) {
    console.error('[watch-limit] Keepalive stop failed:', error);
    return false;
  }
}

export async function isWatchingLimitEnabled(): Promise<boolean> {
  try {
    await fetchFeatureFlags();
  } catch (error) {
    console.warn('[watch-limit] Failed to refresh feature flags, using default disabled state', error);
  }

  const { featureFlags, isFlagEnabled } = useFeatureFlags();
  const defaultValue = false;
  const rawValue = featureFlags.value?.enable_watching_limit;
  const enabled = isFlagEnabled('enable_watching_limit', defaultValue);

  console.warn('[watch-limit] Feature flag resolved', {
    flagName: 'enable_watching_limit',
    rawValue,
    defaultValue,
    enabled,
  });

  return enabled;
}

export function getWatchLimitPayloadFromRoute(
  routeLike: RouteLike,
  cardInfoOverride = getStorage(STORAGE_KEYS.CARD_INFO),
): WatchLimitPayload | null {
  const body = getBody(cardInfoOverride);
  const liveFlag = Number(getQueryValue(routeLike.query?.live)) === 1 ? 1 : 0;

  if (liveFlag === 1) {
    const matchType = Number(body.match_info?.match_type ?? 0) === 1 ? 1 : 0;
    const id =
      getQueryValue(routeLike.query?.match_id) ||
      String(body.match_info?.match_id || getQueryValue(routeLike.query?.mid));
    if (!id) return null;

    return {
      id,
      type: matchType as 0 | 1,
      liveFlag,
    };
  }

  const id =
    getQueryValue(routeLike.query?.mid) ||
    String(
      body.video_info?.vid ||
        body.clip_info?.video?.vid ||
        body.episode_info?.video?.vid ||
        body.movie_info?.video?.vid ||
        body.clip_info?.id ||
        body.episode_info?.id ||
        body.movie_info?.id ||
        '',
    );

  if (!id) return null;

  return {
    id,
    type: 0,
    liveFlag,
  };
}

export async function buildWatchLimitContext(
  routeLike: RouteLike,
  cardInfoOverride = getStorage(STORAGE_KEYS.CARD_INFO),
  subscriptionInfoOverride?: UserSubscribeInfo | null,
): Promise<WatchLimitContext | null> {
  const payload = getWatchLimitPayloadFromRoute(routeLike, cardInfoOverride);
  if (!payload) return null;

  const body = getBody(cardInfoOverride);
  const subscriptionInfo =
    subscriptionInfoOverride !== undefined ? subscriptionInfoOverride : await useSubscriptionStore().ensureLoaded();
  const isSubscribed = !!subscriptionInfo?.is_vip;

  return {
    ...payload,
    contentId: getQueryValue(routeLike.query?.mid) || payload.id,
    videoTitle: getVideoTitle(body, payload.liveFlag),
    videoType: getVideoType(body, payload.liveFlag),
    variant: isSubscribed ? 'subscribed' : 'unsubscribed',
    wallType: isSubscribed ? 'absolute_limit' : 'subscription_redirection',
    subscriptionStatus: isSubscribed ? 'subscribed' : 'unsubscribed',
    subscriptionPlan: isSubscribed ? String(subscriptionInfo?.vip_level ?? '') : 'free',
  };
}

export function queryWatchLimit(payload: WatchLimitPayload) {
  return requestWatchLimit('query', payload);
}

export function startWatchLimit(payload: WatchLimitPayload) {
  return requestWatchLimit('start', payload);
}

export function heartbeatWatchLimit(payload: WatchLimitPayload) {
  return requestWatchLimit('heartbeat', payload);
}

export function stopWatchLimit(payload: WatchLimitPayload) {
  return requestWatchLimit('stop', payload);
}

export function shouldBlockWatchLimitEntry(result: WatchLimitResult) {
  return !result.acquired;
}

export function createWatchLimitSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `watch-limit-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function setPendingWatchLimitContext(context: PendingWatchLimitContext) {
  try {
    sessionStorage.setItem(WATCH_LIMIT_CONTEXT_STORAGE_KEY, JSON.stringify(context));
  } catch (error) {
    console.warn('[watch-limit] Failed to persist pending context', error);
  }
}

export function getPendingWatchLimitContext(): PendingWatchLimitContext | null {
  try {
    const raw = sessionStorage.getItem(WATCH_LIMIT_CONTEXT_STORAGE_KEY);
    if (!raw) return null;

    return JSON.parse(raw) as PendingWatchLimitContext;
  } catch (error) {
    console.warn('[watch-limit] Failed to read pending context', error);
    return null;
  }
}

export function clearPendingWatchLimitContext() {
  try {
    sessionStorage.removeItem(WATCH_LIMIT_CONTEXT_STORAGE_KEY);
  } catch (error) {
    console.warn('[watch-limit] Failed to clear pending context', error);
  }
}
